import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AwardCard } from '@/components/AwardCard';
import { useAwardsByMonth } from '@/hooks/useAwards';
import { Award, Sparkles } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

type FilterType = 'all' | 'fiction' | 'nonfiction' | 'translation' | 'UK' | 'US' | 'Intl';

export default function Awards() {
  const { data: awards = [], isLoading } = useAwardsByMonth();
  const [filter, setFilter] = useState<FilterType>('all');
  const [yearFilter, setYearFilter] = useState<number | null>(null);

  // Get unique years from awards, sorted descending
  const availableYears = Array.from(new Set(awards.map(a => a.year)))
    .sort((a, b) => b - a);

  // Set default year to current year when data loads
  useEffect(() => {
    if (availableYears.length > 0 && yearFilter === null) {
      const currentYear = new Date().getFullYear();
      if (availableYears.includes(currentYear)) {
        setYearFilter(currentYear);
      } else {
        setYearFilter(availableYears[0]); // Most recent year if current year not available
      }
    }
  }, [availableYears.length]);

  const filteredAwards = awards.filter(award => {
    // Apply genre/region filter
    let matchesFilter = true;
    if (filter !== 'all') {
      if (['fiction', 'nonfiction', 'translation'].includes(filter)) {
        matchesFilter = award.genre_focus === filter;
      } else {
        matchesFilter = award.region === filter;
      }
    }
    
    // Apply year filter
    const matchesYear = yearFilter === null || award.year === yearFilter;
    
    return matchesFilter && matchesYear;
  });

  return (
    <div className="w-full max-w-full overflow-x-hidden">
      <main className="container mx-auto px-4 py-8 max-w-7xl w-full">
        {/* Hero Section */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Award className="w-8 h-8 text-primary" />
            <h1 className="text-4xl font-bold">Book Awards Shortlists</h1>
          </div>
          <p className="text-lg text-muted-foreground mb-6">
            Explore the most prestigious literary prizes and discover shortlisted books that match your reading taste
          </p>
          
          <Link to="/awards/matches">
            <Button className="gap-2">
              <Sparkles className="w-4 h-4" />
              Which shortlist suits you?
            </Button>
          </Link>
        </div>

        {/* Filter Chips */}
        <div className="space-y-6 mb-8">
          <div>
            <p className="text-sm font-medium text-muted-foreground mb-3">Genre & Region</p>
            <div className="flex gap-2 overflow-x-auto pb-2">
              <Badge
                variant={filter === 'all' ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => setFilter('all')}
              >
                All
              </Badge>
              <Badge
                variant={filter === 'fiction' ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => setFilter('fiction')}
              >
                Fiction
              </Badge>
              <Badge
                variant={filter === 'nonfiction' ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => setFilter('nonfiction')}
              >
                Nonfiction
              </Badge>
              <Badge
                variant={filter === 'translation' ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => setFilter('translation')}
              >
                Translation
              </Badge>
              <Badge
                variant={filter === 'UK' ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => setFilter('UK')}
              >
                UK
              </Badge>
              <Badge
                variant={filter === 'US' ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => setFilter('US')}
              >
                US
              </Badge>
              <Badge
                variant={filter === 'Intl' ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => setFilter('Intl')}
              >
                International
              </Badge>
            </div>
          </div>

          {availableYears.length > 0 && (
            <div className="border-t pt-6">
              <p className="text-base font-semibold mb-3">Year</p>
              <Select 
                value={yearFilter?.toString() || 'all'} 
                onValueChange={(value) => setYearFilter(value === 'all' ? null : parseInt(value))}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Select year" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Years</SelectItem>
                  {availableYears.map(year => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Awards Grid */}
        {isLoading ? (
          <div className="flex gap-4 overflow-x-auto pb-4">
            {[1, 2, 3, 4].map(i => (
              <Skeleton key={i} className="w-[320px] h-[280px] flex-shrink-0" />
            ))}
          </div>
        ) : (
          <div className="flex gap-4 overflow-x-auto pb-4">
            {filteredAwards.map(award => (
              <AwardCard key={award.id} award={award} />
            ))}
          </div>
        )}

        {!isLoading && filteredAwards.length === 0 && (
          <div className="text-center py-12">
            <Award className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <p className="text-lg text-muted-foreground">No awards found for this filter</p>
          </div>
        )}
      </main>
    </div>
  );
}
